<script setup>
import { computed } from "vue";
import { Document, Download } from "@element-plus/icons-vue";
import SocialFundApplicationService from "@/api/services/socialFundApplication.service";
import Notification from "@/utils/plugins/NotificationService";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  reference: {
    type: String,
    default: "",
  },
  documents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update:modelValue"]);

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const close = () => {
  visible.value = false;
};

const statusLabel = {
  validated: "Validado",
  pending: "Pendiente",
  rejected: "Rechazado",
};

// FS-S01: downloadUrl ya no es una ruta pública estática, es un endpoint
// autenticado — hay que pedirlo con el JWT (Api()) y descargar el blob,
// un <a href> plano no puede llevar el header Authorization.
const downloadDocument = (document) => {
  if (!document.downloadUrl) return;

  SocialFundApplicationService.downloadDocument(document.downloadUrl)
    .then((response) => {
      const blob = new Blob([response.data]);
      const link = window.document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = document.originalName || document.name;
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch(() => {
      Notification.addNotification("Error al descargar el documento.", "error", 6000);
    });
};
</script>

<template>
  <el-dialog v-model="visible" width="650px" :show-close="false" :lock-scroll="true"
    class="social-fund-request-documents-dialog">
    <section v-if="visible" class="social-fund-request-documents-dialog__content">
      <img class="social-fund-request-documents-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
        alt="Cerrar" @click="close" />
      <header class="social-fund-request-documents-dialog__header">
        <el-icon>
          <Document />
        </el-icon>
        <div>
          <h2>Documentación de la solicitud</h2>
          <p>{{ reference }}</p>
        </div>
      </header>

      <div v-if="documents.length" class="social-fund-request-documents-dialog__list">
        <article v-for="document in documents" :key="document.id" class="social-fund-request-documents-dialog__item">
          <span class="social-fund-request-documents-dialog__file-icon"><el-icon>
              <Document />
            </el-icon></span>
          <div class="social-fund-request-documents-dialog__file-data">
            <strong>{{ document.originalName || document.name }}</strong>
            <small>{{ document.name }}</small>
            <span v-if="document.status" :class="`social-fund-request-documents-dialog__status--${document.status}`">{{
              statusLabel[document.status] || document.status }}</span>
          </div>
          <button v-if="document.downloadUrl" type="button" @click="downloadDocument(document)"
            class="social-fund-request-documents-dialog__download">
            <el-icon>
              <Download />
            </el-icon>DESCARGAR
          </button>
          <span v-else class="social-fund-request-documents-dialog__unavailable">No disponible</span>
        </article>
      </div>
      <p v-else class="social-fund-request-documents-dialog__empty">Esta solicitud no tiene documentos adjuntos.</p>

      <footer class="social-fund-request-documents-dialog__actions">
        <button type="button" class="btn btn_yellow" @click="close">CERRAR</button>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
:global(.el-dialog.social-fund-request-documents-dialog) {
  background: #f5f3ff;
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.social-fund-request-documents-dialog :deep(.el-dialog__header) {
  display: none;
}

.social-fund-request-documents-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.social-fund-request-documents-dialog__content {
  color: #263052;
  padding: 20px 24px 16px;
  position: relative;
}

.social-fund-request-documents-dialog__close {
  height: 38px;
  object-fit: contain;
  position: absolute;
  right: 14px;
  top: 13px;
  width: 38px;
}

.social-fund-request-documents-dialog__header {
  align-items: center;
  display: flex;
  gap: 12px;
  padding-right: 42px;
}

.social-fund-request-documents-dialog__header>.el-icon {
  color: $bg-blue;
  font-size: 31px;
}

.social-fund-request-documents-dialog__header h2 {
  color: $bg-blue;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -.05em;
  line-height: 1.1;
  margin: 0;
}

.social-fund-request-documents-dialog__header p {
  color: #f9156f;
  font-size: 13px;
  font-weight: 800;
  margin: 4px 0 0;
}

.social-fund-request-documents-dialog__list {
  display: grid;
  gap: 10px;
  margin-top: 22px;
}

.social-fund-request-documents-dialog__item {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dce8;
  border-radius: 10px;
  display: flex;
  gap: 12px;
  min-height: 78px;
  padding: 11px 13px;
}

.social-fund-request-documents-dialog__file-icon {
  align-items: center;
  background: #e9ebff;
  border-radius: 10px;
  color: $bg-blue;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 25px;
  height: 45px;
  justify-content: center;
  width: 45px;
}

.social-fund-request-documents-dialog__file-data {
  display: grid;
  min-width: 0;
}

.social-fund-request-documents-dialog__file-data strong {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.social-fund-request-documents-dialog__file-data small {
  color: #737b94;
  font-size: 11px;
  margin-top: 3px;
}

.social-fund-request-documents-dialog__file-data span {
  border-radius: 10px;
  font-size: 10px;
  font-weight: 800;
  margin-top: 5px;
  padding: 3px 7px;
  width: fit-content;
}

.social-fund-request-documents-dialog__status--validated {
  background: #effbf4;
  color: #279653;
}

.social-fund-request-documents-dialog__status--pending {
  background: #fffaf0;
  color: #bf7c00;
}

.social-fund-request-documents-dialog__status--rejected {
  background: #f7f7f8;
  color: #757a88;
}

.social-fund-request-documents-dialog__download {
  align-items: center;
  background: none;
  border: none;
  color: $bg-blue;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  gap: 7px;
  margin-left: auto;
  padding: 0;
  text-decoration: none;
}

.social-fund-request-documents-dialog__download:hover {
  color: #f9156f;
}

.social-fund-request-documents-dialog__download .el-icon {
  font-size: 18px;
}

.social-fund-request-documents-dialog__unavailable {
  color: #7b8398;
  font-size: 11px;
  margin-left: auto;
}

.social-fund-request-documents-dialog__empty {
  color: #737b94;
  font-size: 14px;
  margin: 28px 0;
  text-align: center;
}

.social-fund-request-documents-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  justify-content: flex-end;
  margin: 20px -24px -16px;
  padding: 15px 24px;
}

.social-fund-request-documents-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  min-width: 160px;
}

.social-fund-request-documents-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

@media (max-width: 767px) {
  .social-fund-request-documents-dialog__content {
    padding: 16px 16px 14px;
  }

  .social-fund-request-documents-dialog__close {
    right: 8px;
    top: 8px;
  }

  .social-fund-request-documents-dialog__header h2 {
    font-size: 24px;
  }

  .social-fund-request-documents-dialog__item {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .social-fund-request-documents-dialog__file-data {
    flex: 1;
  }

  .social-fund-request-documents-dialog__download,
  .social-fund-request-documents-dialog__unavailable {
    margin-left: 57px;
  }

  .social-fund-request-documents-dialog__actions {
    margin: 18px -16px -14px;
    padding: 14px 16px;
  }

  .social-fund-request-documents-dialog__actions .btn {
    width: 100%;
  }
}
</style>
